/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Layout from "../../../Layouts/Layout";
import HintBox from "../../../../components/Box/HintBox/HintBox";
import Container from "../../../../components/Container/Container";
import LegalbirdLoader from "../../../../components/ContentLoader/LegalbirdLoader";
import { Link, Navigate } from "react-router";
import Button from "@mui/material/Button";
import { getCaseLink, getProductData, getProductNameBy } from "../../../../services/productService";
import { FormProvider } from "../../../../provider/Form/FormProvider";
import _cloneDeep from "lodash/cloneDeep";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCollection, fetchResource, queryKeys, updateResource } from "../../../../services/reactQuery/reactQueryService";
import useProductParams from "../../../../hooks/useProductParams";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import { InquiryToCustomerStatus } from "../../../../types/Entities/InquiryToCustomer";
import { STATUS_ADMINISTRATIVE_DECISION_ISSUED } from "../../../../services/administrativeLawLitigationCase/leadStatus";
import AdministrativeDecisionFeedbackForm from "../../../../components/Form/AbstractAdministrativeLawLitigationCase/AdministrativeDecisionFeedbackForm/AdministrativeDecisionFeedbackForm";

const AdministrativeDecisionFeedbackPage = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId)
  });
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });

  const inquiryFilters = {
    identifier: "administrativeDecisionFeedbackInquiry",
    productId: productId,
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
    "status[2]": InquiryToCustomerStatus.STATUS_IN_PROGRESS,
  };
  const inquiryApiUri = "/inquiry_to_customers";
  const { data: inquiries } = useQuery(
    {
      queryKey: queryKeys.collection(inquiryApiUri, inquiryFilters),
      queryFn: () => fetchCollection(inquiryApiUri, inquiryFilters),
      enabled: !!product,
    }
  );

  if (isLoading || !product || !inquiries) {
    return (
      <Layout>
        <Container>
          <Container sx={{ width: 250 }}>
            <LegalbirdLoader />
          </Container>
        </Container>
      </Layout>
    );
  }

  if (!!product.administrativeDecisionStatusClient || product.leadStatus !== STATUS_ADMINISTRATIVE_DECISION_ISSUED) {
    return (
      <Layout>
        <Container>
          <HintBox headline={"Vielen Dank für Ihre Rückmeldung!"}>
            Wir haben Ihre Rückmeldung erhalten und werden Ihren Fall entsprechend weiter bearbeiten. Sie werden über das weitere Vorgehen wie gewohnt per
            Nachricht und Ihre Servicewelt informiert.
            <Button component={Link} to={getCaseLink(product)} variant={"text"} color={"primary"}>
              Zurück zum Fall
            </Button>
          </HintBox>
        </Container>
      </Layout>
    );
  }

  const currentInquiryToCustomer = inquiries[0];

  if (!currentInquiryToCustomer || currentInquiryToCustomer.identifier !== "administrativeDecisionFeedbackInquiry") {
    return <Navigate to={getCaseLink(product)} />;
  }

  const getHintText = (product: AbstractCase) => {
    return (
      <p>
        <strong>
          {product.administrativeProceedingsLetterSentDate !== null
            ? "Die Behörde hat auf unser Schreiben mit einem Bescheid reagiert."
            : "zu Ihrem Verfahren liegt ein Bescheid der Behörde vor."}
        </strong>
        <br />
        <br />
        Wir benötigen als nächstes Ihre Rückmeldung darüber, wie Sie weiter vorgehen möchten.
      </p>
    );
  };

  const initialValues = {
    administrativeDecisionStatusClient: null,
    administrativeDecisionFeedbackClient: null,
    additionalCustomerNote: "",
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    setIsLoadingInternal(true);

    const updateData = _cloneDeep(values);

    if (updateData.additionalCustomerNote === "") {
      updateData.additionalCustomerNote = null;
    }

    if (updateData.administrativeDecisionStatusClient === "fullyAgreed") {
      updateData.administrativeDecisionFeedbackClient = "accept";
    }

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: updateData,
    });

    setIsLoadingInternal(false);
  };

  return (
    <Layout backgroundType={"serviceworld"}>
      <Container>
        <HintBox headline={"Ihre Rückmeldung wird benötigt"}>{getHintText(product)}</HintBox>
        <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
          <AdministrativeDecisionFeedbackForm product={product} isLoading={isLoadingInternal} />
        </FormProvider>
      </Container>
    </Layout>
  );
};

export default AdministrativeDecisionFeedbackPage;
